/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { isPhoneNumberDefault, isRequired, requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import Person from "@/src/components/Form/Common/Person/Person";
import Address from "@/src/components/Form/Common/Address/Address";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";

type LegalRepresentativeProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function LegalRepresentative({ submitProps }: LegalRepresentativeProps) {
  const { values, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Persönliche Angaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasLegalRepresentative"}
                question={"Haben Sie einen gesetzlichen Vertreter?"}
                modalContent={
                  <p>
                    Gesetzliche Vertreter sind zum Beispiel die Eltern für ihre minderjährigen Kinder oder ein vom Gericht bestellter Betreuer. Gemeint ist hier{" "}
                    <strong>nicht</strong> der Anwalt, den Sie beauftragt haben.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasLegalRepresentative === "yes" && (
              <>
                <Person path="legalRepresentative" headline="Wie heißt Ihr gesetzlicher Vertreter?" />
                <Address path="legalRepresentative.addresses[0]" headline="Wo wohnt Ihr gesetzlicher Vertreter?" />
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Wie kann man Ihren gesetzlichen Vertreter telefonisch erreichen?</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="telephoneRepresentative"
                    label="Telefonnummer"
                    autoComplete="tel"
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie Ihre Telefonnummer an",
                      },
                      ...isPhoneNumberDefault,
                    ]}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
